import {
  enquiryFields,
  questionSetFields,
  questionFields,
  riskProfileFields,
} from "kw/api/fragments";
import { defaultQuestionFlowKnockoutAction } from "kw/components/QuestionFlow/QuestionFlowComponent";
import { useStartQuestionFlow } from "kw/hooks/useStartQuestionFlow";
import { getQuestionSetAnswers } from "kw/util/getQuestionSetAnswers";
import { QuestionSet } from "kw/util/QuestionSet";
import React, { FC, useCallback, useEffect, useState } from "react";

export const QuoteSummarySection: FC<{
  riskProfileId: string;
  priceLoading?: boolean;
  priceRiskProfile?: () => void;
}> = ({ riskProfileId, priceLoading, priceRiskProfile }) => {
  const {
    enquiry,
    questionSet,
    profileData,
    refresh: refreshStartQuoteSummary,
    answerEnquiry,
    loading,
    saving: submittingAnswer,
  } = useStartQuestionFlow({
    mutationQuery: getStartQuoteSummaryQuery(riskProfileId),
    globalDataKey: "quoting",
    questionFlowResponseKey: "startQuoteSummary",
    onQuestionFlowKnockout: defaultQuestionFlowKnockoutAction,
  });

  const [localQuestionSet, setLocalQuestionSet] = useState<TQuestionSet>();
  const initialValues = getQuestionSetAnswers(questionSet, profileData);

  const onChange = useCallback(
    (values: unknown) => {
      const result = {};
      for (const [k, v] of Object.entries(values)) {
        const question = questionSet?.questions.find((q) => q.id == k);
        result[question.storagePath] = v;
      }
      answerEnquiry(enquiry?.questionFlow?.id, result, () => {
        priceRiskProfile();
        refreshStartQuoteSummary();
      });
    },
    [enquiry?.questionFlow?.id]
  );

  useEffect(() => {
    // answerEnquiry returns empty questionSet from useQuestionFlow hook and it results
    // in questionSet not getting displayed.
    // Updating questionSet when it has value doesn't update questionSet upon answerEnquiry
    if (questionSet) {
      setLocalQuestionSet(questionSet);
    }
  }, [questionSet]);

  return (
    <QuestionSet
      questionSet={localQuestionSet}
      disabled={loading || priceLoading || submittingAnswer}
      initialValues={initialValues}
      onChange={onChange}
      key={questionSet?.id}
      noNextButton={true}
    />
  );
};

const getStartQuoteSummaryQuery = (riskProfileId: string) => {
  return {
    startQuoteSummary: [
      {
        input: {
          riskProfileId: riskProfileId,
        },
      },
      {
        currentEnquiry: {
          ...enquiryFields,
          questionSet: {
            ...questionSetFields,
            questions: {
              ...questionFields,
            },
          },
        },
        riskProfile: {
          ...riskProfileFields,
          quotes: {
            id: true,
            installments: {
              kind: true,
              inclusivePrice: true,
            },
            periodicPrice: true,
            paymentFrequency: true,
            product: {
              description: true,
              paymentGateway: true,
              skipPayments: true,
              name: true,
              key: true,
            },
            state: true,
          },
        },
        history: {
          questionSet: {
            id: true,
            key: true,
            label: true,
          },
        },
        maxSets: true,
        currentSet: true,
      },
    ],
  };
};
